import React, { useState } from 'react';
import { bikes, bikeParts, stations, spareParts, masterSpareParts, customers } from '../services/api';

const Templates = () => {
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [importErrors, setImportErrors] = useState([]); // row-level validation errors

  const handleDownload = async (type) => {
    setDownloadLoading(true);
    try {
      let response;
      let filename = '';
      if (type === 'bikes') {
        response = await bikes.getTemplate();
        filename = 'template-motor.xlsx';
      } else if (type === 'parts') {
        response = await bikeParts.getTemplate();
        filename = 'template-sparepart.xlsx';
      } else if (type === 'spare-parts') {
        response = await spareParts.getTemplate();
        filename = 'template-master-sparepart.xlsx';
      } else if (type === 'master-spare-parts') {
        response = await masterSpareParts.getTemplate();
        filename = 'template-global-master-sparepart.xlsx';
      } else if (type === 'stations') {
        response = await stations.getTemplate();
        filename = 'template-ahass.xlsx';
      } else if (type === 'customers') {
        response = await customers.getTemplate();
        filename = 'template-customer.xlsx';
      }

      // If the response is actually JSON error disguised as a blob
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const error = JSON.parse(text);
        throw new Error(error.message || 'Gagal mengunduh template.');
      }

      const url = window.URL.createObjectURL(new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (err) {
      console.error('Download failed', err);
      const errorMessage = err.message || 'Gagal mengunduh template.';
      alert(errorMessage);
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleImport = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setLoading(true);
    setMessage({ type: '', text: '' });
    setImportErrors([]);

    try {
      let res;
      if (type === 'bikes') res = await bikes.import(formData);
      else if (type === 'spare-parts') res = await spareParts.import(formData);
      else if (type === 'master-spare-parts') res = await masterSpareParts.import(formData);
      else if (type === 'parts') res = await bikeParts.import(formData);
      else if (type === 'stations') res = await stations.import(formData);
      else if (type === 'customers') res = await customers.import(formData);
      else if (type === 'parts-images') res = await bikeParts.importImages(formData);
      else if (type === 'spareparts-images') res = await spareParts.importImages(formData);

      setMessage({ type: 'success', text: res.data.message || 'Berhasil mengimpor data.' });
    } catch (err) {
      console.error('Import failed', err);
      const resData = err.response?.data;

      // Show row-level validation errors if available (422)
      if (resData?.errors && Array.isArray(resData.errors) && resData.errors.length > 0) {
        setImportErrors(resData.errors);
      }

      // Build a human-readable summary message
      let errText = resData?.message || 'Gagal mengimpor data.';
      if (resData?.error) {
        // 500 error with detail object
        const detail = typeof resData.error === 'object'
          ? `${resData.error.exception}: ${resData.error.message} (${resData.error.file}:${resData.error.line})`
          : resData.error;
        errText += ' — ' + detail;
      }

      setMessage({ type: 'error', text: errText });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="p-6 border-b border-gray-100 bg-gray-50">
        <h2 className="text-xl font-bold text-gray-800">Manajemen Template & Import</h2>
        <p className="text-sm text-gray-500 mt-1">Unduh template Excel/CSV dan unggah data Anda di sini.</p>
      </div>

      <div className="p-6">

        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
            <span className="font-medium">{message.text}</span>
          </div>
        )}

        {/* Concrete import validation errors */}
        {importErrors.length > 0 && (
          <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200 text-red-700">
            <div className="font-bold mb-2">Detail Error Import:</div>
            <ul className="text-xs max-h-60 overflow-auto">
              {importErrors.slice(0, 20).map((err, idx) => (
                <li key={idx} className="mb-1">
                  Baris <b>{err.row ?? '-'}</b> kolom <b>{err.column ?? '-'}</b>{' '}
                  {err.value !== undefined && <span>(<i>{err.value}</i>)</span>}:<br />
                  {err.errors && Array.isArray(err.errors)
                    ? err.errors.map((e, i) => (
                        <span key={i}>- {e}<br /></span>
                      ))
                    : <span>- {err.errors || 'Error tidak diketahui'}<br /></span>}
                </li>
              ))}
            </ul>
            {importErrors.length > 20 && (
              <div className="mt-2 text-xs italic text-red-600">dan {importErrors.length - 20} error lainnya...</div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Section for Bikes */}
          <div className="border border-gray-200 rounded-xl p-5 hover:border-blue-300 transition-colors">
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Data Motor & Kode
            </h3>
            <p className="text-xs text-gray-500 mb-4 h-12">Gunakan ini untuk menambah mapping kode motor (SC, SG, dll) berdasarkan tahun.</p>
            <div className="flex flex-col gap-2">
              <button 
                onClick={() => handleDownload('bikes')}
                className={`w-full text-sm ${downloadLoading ? 'bg-gray-100 text-gray-400' : 'bg-blue-50 text-blue-700'} font-bold py-2 rounded-lg hover:bg-blue-100 transition`}
              >
                Unduh Template
              </button>
              <label className={`w-full text-sm font-bold py-2 rounded-lg text-center cursor-pointer transition ${
                loading ? 'bg-gray-100 text-gray-400' : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}>
                {loading ? 'Mengimpor...' : 'Unggah CSV/Excel'}
                <input 
                  type="file" 
                  className="hidden" 
                  accept=".csv,.xlsx" 
                  onChange={(e) => handleImport(e, 'bikes')}
                  disabled={loading}
                />
              </label>
            </div>
          </div>

          {/* Section for Spare Parts (Master) */}
          <div className="border border-gray-200 rounded-xl p-5 hover:border-blue-300 transition-colors">
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              Master Spare Part
            </h3>
            <p className="text-xs text-gray-500 mb-4 h-12">Kelola master data spare part global (nama, deskripsi, default image).</p>
            <div className="flex flex-col gap-2">
              <button 
                onClick={() => handleDownload('spare-parts')}
                className={`w-full text-sm ${downloadLoading ? 'bg-gray-100 text-gray-400' : 'bg-blue-50 text-blue-700'} font-bold py-2 rounded-lg hover:bg-blue-100 transition`}
              >
                Unduh Template
              </button>
              <label className={`w-full text-sm font-bold py-2 rounded-lg text-center cursor-pointer transition ${
                loading ? 'bg-gray-100 text-gray-400' : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}>
                Unggah Excel/CSV
                <input 
                  type="file" 
                  className="hidden" 
                  accept=".csv,.xlsx" 
                  onChange={(e) => handleImport(e, 'spare-parts')}
                  disabled={loading}
                />
              </label>
              <label className={`w-full text-sm font-bold py-2 rounded-lg text-center cursor-pointer transition ${
                loading ? 'bg-gray-100 text-gray-400' : 'bg-green-600 text-white hover:bg-green-700'
              }`}>
                Import Gambar (ZIP)
                <input 
                  type="file" 
                  className="hidden" 
                  accept=".zip" 
                  onChange={(e) => handleImport(e, 'spareparts-images')}
                  disabled={loading}
                />
              </label>
            </div>
          </div>

          {/* Section for Parts Mapping */}
          <div className="border border-gray-200 rounded-xl p-5 hover:border-blue-300 transition-colors">
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Mapping & Part Number
            </h3>
            <p className="text-xs text-gray-500 mb-4 h-12">Mapping spare part ke motor spesifik, set harga, & part number.</p>
            <div className="flex flex-col gap-2">
              <button 
                onClick={() => handleDownload('parts')}
                className={`w-full text-sm ${downloadLoading ? 'bg-gray-100 text-gray-400' : 'bg-blue-50 text-blue-700'} font-bold py-2 rounded-lg hover:bg-blue-100 transition`}
              >
                Unduh Template
              </button>
              <label className={`w-full text-sm font-bold py-2 rounded-lg text-center cursor-pointer transition ${
                loading ? 'bg-gray-100 text-gray-400' : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}>
                Unggah Excel/CSV
                <input 
                  type="file" 
                  className="hidden" 
                  accept=".csv,.xlsx" 
                  onChange={(e) => handleImport(e, 'parts')}
                  disabled={loading}
                />
              </label>
              <label className={`w-full text-sm font-bold py-2 rounded-lg text-center cursor-pointer transition ${
                loading ? 'bg-gray-100 text-gray-400' : 'bg-green-600 text-white hover:bg-green-700'
              }`}>
                Import Gambar (ZIP)
                <input 
                  type="file" 
                  className="hidden" 
                  accept=".zip" 
                  onChange={(e) => handleImport(e, 'parts-images')}
                  disabled={loading}
                />
              </label>
            </div>
          </div>

          {/* Section for Global Master Spare Parts */}
          <div className="border border-gray-200 rounded-xl p-5 hover:border-blue-300 transition-colors">
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Global Master Spare Parts
            </h3>
            <p className="text-xs text-gray-500 mb-4 h-12">Kelola perpustakaan global spare part dengan nomor bagian, harga, dan deskripsi.</p>
            <div className="flex flex-col gap-2">
              <button 
                onClick={() => handleDownload('master-spare-parts')}
                className={`w-full text-sm ${downloadLoading ? 'bg-gray-100 text-gray-400' : 'bg-blue-50 text-blue-700'} font-bold py-2 rounded-lg hover:bg-blue-100 transition`}
              >
                Unduh Template
              </button>
              <label className={`w-full text-sm font-bold py-2 rounded-lg text-center cursor-pointer transition ${
                loading ? 'bg-gray-100 text-gray-400' : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}>
                Unggah Excel/CSV
                <input 
                  type="file" 
                  className="hidden" 
                  accept=".csv,.xlsx" 
                  onChange={(e) => handleImport(e, 'master-spare-parts')}
                  disabled={loading}
                />
              </label>
            </div>
          </div>

          {/* Section for Stations */}
          <div className="border border-gray-200 rounded-xl p-5 hover:border-blue-300 transition-colors">
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Data AHASS (Station)
            </h3>
            <p className="text-xs text-gray-500 mb-4 h-12">Kelola data cabang AHASS/Station yang terdaftar di sistem.</p>
            <div className="flex flex-col gap-2">
              <button 
                onClick={() => handleDownload('stations')}
                className={`w-full text-sm ${downloadLoading ? 'bg-gray-100 text-gray-400' : 'bg-blue-50 text-blue-700'} font-bold py-2 rounded-lg hover:bg-blue-100 transition`}
              >
                Unduh Template
              </button>
              <label className={`w-full text-sm font-bold py-2 rounded-lg text-center cursor-pointer transition ${
                loading ? 'bg-gray-100 text-gray-400' : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}>
                Unggah Excel/CSV
                <input 
                  type="file" 
                  className="hidden" 
                  accept=".csv,.xlsx" 
                  onChange={(e) => handleImport(e, 'stations')}
                  disabled={loading}
                />
              </label>
            </div>
          </div>

          {/* Section for Customers */}
          <div className="border border-gray-200 rounded-xl p-5 hover:border-blue-300 transition-colors">
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-2a6 6 0 0112 0v2zm0 0h6v-2a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              Data Pelanggan (Customer)
            </h3>
            <p className="text-xs text-gray-500 mb-4 h-12">Kelola data pelanggan (customer), nomor mesin, nomor rangka/ATPM, dan tahun rakitan.</p>
            <div className="flex flex-col gap-2">
              <button 
                onClick={() => handleDownload('customers')}
                className={`w-full text-sm ${downloadLoading ? 'bg-gray-100 text-gray-400' : 'bg-blue-50 text-blue-700'} font-bold py-2 rounded-lg hover:bg-blue-100 transition`}
              >
                Unduh Template
              </button>
              <label className={`w-full text-sm font-bold py-2 rounded-lg text-center cursor-pointer transition ${
                loading ? 'bg-gray-100 text-gray-400' : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}>
                {loading ? 'Mengimpor...' : 'Unggah CSV/Excel'}
                <input 
                  type="file" 
                  className="hidden" 
                  accept=".csv,.xlsx" 
                  onChange={(e) => handleImport(e, 'customers')}
                  disabled={loading}
                />
              </label>
            </div>
          </div>
        </div>

        {/* Instructions in Bahasa Indonesia */}
        <div className="mt-10 bg-blue-50 border border-blue-100 rounded-xl p-6">
          <h4 className="font-bold text-blue-800 mb-4 flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Instruksi Penggunaan Template & Import (Penting!)
          </h4>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h5 className="font-bold text-blue-700 underline">1. Import Data (Excel/CSV)</h5>
              <ol className="list-decimal list-inside space-y-3 text-sm text-blue-900 pl-2">
                <li>
                  <strong>Selalu gunakan template terbaru:</strong> Pastikan Anda mengunduh template terbaru sebelum melakukan pengisian data. Format kolom tidak boleh diubah.
                </li>
                <li>
                  <strong>Master Spare Part:</strong>
                  <ul className="list-disc list-inside ml-6 mt-1 text-gray-700 space-y-1">
                    <li>Kolom <code>name</code> diisi Nama Spare Part.</li>
                    <li>Kolom <code>description</code> diisi Deskripsi Part.</li>
                    <li>Kolom <code>is_miscellaneous</code> diisi 1 jika part insidental, 0 jika part utama.</li>
                  </ul>
                </li>
                <li>
                  <strong>Data AHASS (Station):</strong>
                  <ul className="list-disc list-inside ml-6 mt-1 text-gray-700 space-y-1">
                    <li>Kolom <code>code</code> diisi Kode AHASS.</li>
                    <li>Kolom <code>name</code> diisi Nama Cabang.</li>
                    <li>Kolom <code>city</code> diisi Nama Kota.</li>
                    <li>Kolom <code>contact_name</code> & <code>contact_phone</code> diisi untuk data kontak Service Advisor/Admin.</li>
                  </ul>
                </li>
                <li>
                  <strong>Data Motor & Kode:</strong>
                  <ul className="list-disc list-inside ml-6 mt-1 text-gray-700 space-y-1">
                    <li>Kolom <code>name</code> diisi Nama Motor (contoh: SCOOPY, VARIO 125).</li>
                    <li>Kolom <code>code</code> diisi Kode Motor (contoh: SC, SG).</li>
                    <li>Kolom <code>start_year</code> dan <code>end_year</code> diisi rentang tahun produksi.</li>
                  </ul>
                </li>
                <li>
                  <strong>Mapping & Part Number:</strong>
                  <ul className="list-disc list-inside ml-6 mt-1 text-gray-700 space-y-1">
                    <li>Kolom <code>bike_code</code> diisi Kode Motor (contoh: K2S, KZR). Pastikan kode valid.</li>
                    <li>Kolom <code>spare_part_name</code> diisi Nama Master Spare Part (contoh: Ban Luar).</li>
                    <li>Kolom <code>part_number</code>, <code>prices</code> (harga HET), dan <code>type</code> diisi sesuai.</li>
                  </ul>
                </li>
                <li>
                  <strong>Data Pelanggan (Customer):</strong>
                  <ul className="list-disc list-inside ml-6 mt-1 text-gray-700 space-y-1">
                    <li>Kolom <code>customer_name</code> diisi Nama Pelanggan (wajib).</li>
                    <li>Kolom <code>vehicle_number</code> diisi Nomor Plat Kendaraan, contoh: <code>B1234XYZ</code> (wajib).</li>
                    <li>Kolom <code>engine_number</code> diisi Nomor Mesin kendaraan (unik, maks. 12 karakter, wajib).</li>
                    <li>Kolom <code>customer_phone_number</code> diisi Nomor Telepon/WhatsApp pelanggan (wajib).</li>
                    <li>Kolom <code>atpm</code> diisi Kode ATPM kendaraan, contoh: <code>SCX</code>. <strong>2 karakter pertama</strong> akan dicocokkan ke Master Motor (SC = Scoopy, dst.).</li>
                    <li>Kolom <code>customer_assembly_year</code> diisi Tahun Perakitan motor, contoh: <code>2022</code> (wajib).</li>
                    <li>Kolom <code>last_km</code> diisi KM terakhir kendaraan (opsional).</li>
                  </ul>
                </li>
                <li>
                  <strong>Format File:</strong> Pastikan file disimpan dalam format <code>.xlsx</code> (Excel) atau <code>.csv</code>.
                </li>
                <li>
                  <strong>Data Duplikat:</strong> Jika mengunggah data yang sudah ada (Nama + Kode + Tahun yang sama), sistem akan memperlakukan sebagai data baru atau memberikan error tergantung konfigurasi database.
                </li>
              </ol>
            </div>

            <div className="space-y-4">
              <h5 className="font-bold text-blue-700 underline">2. Import Gambar (ZIP)</h5>
              <ol className="list-decimal list-inside space-y-3 text-sm text-blue-900 pl-2">
                <li>
                  <strong>Format File:</strong> Wajib menggunakan format <code>.zip</code>. Di dalamnya berisi file gambar dengan format <code>.jpg</code>, <code>.jpeg</code>, atau <code>.png</code>.
                </li>
                <li>
                  <strong>Penamaan File (Sangat Penting):</strong>
                  <ul className="list-disc list-inside ml-6 mt-1 text-gray-700 space-y-2">
                    <li>
                      <strong>Master Spare Part:</strong> Nama file harus sama dengan Nama Spare Part di sistem.<br/>
                      Contoh: <code>Ban Depan.jpg</code>, <code>Oli Mesin.png</code>.
                    </li>
                    <li>
                      <strong>Mapping & Part Number:</strong> Nama file harus sama dengan Part Number yang terdaftar. Gambar ini akan otomatis diterapkan ke semua motor yang menggunakan part number tersebut.<br/>
                      Contoh: <code>45150-KZR-601.jpg</code>.
                    </li>
                  </ul>
                </li>
                <li>
                  <strong>Ukuran ZIP:</strong> Maksimal ukuran file ZIP adalah 50MB. Pastikan gambar sudah dikompresi untuk hasil terbaik.
                </li>
                <li>
                  <strong>Struktur ZIP:</strong> File gambar harus berada di root folder ZIP (jangan dimasukkan ke dalam sub-folder).
                </li>
              </ol>
            </div>
          </div>
          <div className="mt-6 p-3 bg-red-50 border border-red-100 rounded-lg text-xs text-red-700 italic">
            * Catatan: Jika ada kesalahan penamaan file, sistem akan melewati (skip) file tersebut dan menampilkan laporan kegagalan setelah proses selesai.
          </div>
        </div>
      </div>
    </div>
  );
};

export default Templates;
